/*
  Sales rep quick-add modal.

  Ported from catchmasterpro-shopify (assets/sales-rep-quick-add.js). Differences,
  all deliberate:

  - The pro theme drives this from MarketPlus "call for price" buttons
    (.mp-hide-price-call-button*) injected by a hide-price app embed. That app is
    not installed on this store, so those selectors, dismissNativeCallPopup(), and
    replaceCallForPriceCopy() (which rewrote "Call For Price" text across the whole
    document) are all dropped.
  - The pro theme gates grid triggers on #ProductGridContainer. Not applicable here;
    [data-sales-rep-trigger] works anywhere on the page.
  - The pro theme observes all of document.body for late-added triggers. This page
    re-renders its own card grid, so instead of a global MutationObserver the
    section calls CatchmasterSalesRep.enhance(node) after each render.

  Triggers: any element with [data-sales-rep-trigger] or id="contact-rep-trigger".
  Optional context: data-product-title and data-product-image on the trigger.
*/
(function () {
  const modal = document.querySelector('[data-sales-rep-quick-add]');
  if (!modal) return;

  /* Rep roster lives in the catchmasterpro store's Shopify Files. Cross-store
     dependency: if that file is re-uploaded the ?v= URL rotates and lookup breaks
     (the UI degrades to the "unavailable" status rather than erroring). */
  const REPS_URL =
    'https://cdn.shopify.com/s/files/1/0688/3465/6495/files/reps_data_cleaned_038cd211-31d0-4bc1-b0a2-72e61656dfb1.json?v=1786639508';
  const TRIGGER_SELECTOR = '[data-sales-rep-trigger], #contact-rep-trigger';

  const dialog = modal.querySelector('.sales-rep-quick-add__dialog');
  const stateSelect = modal.querySelector('[data-sales-rep-state]');
  const statusEl = modal.querySelector('[data-sales-rep-status]');
  const repList = modal.querySelector('[data-sales-rep-list]');
  const productImage = modal.querySelector('[data-sales-rep-product-image]');
  const productTitle = modal.querySelector('[data-sales-rep-product-title]');
  let repsData = [];
  let repsRequest = null;
  let lastFocusedElement = null;
  let activeTrigger = null;

  function setStatus(message) {
    statusEl.textContent = message;
  }

  function clearRepList() {
    repList.replaceChildren();
  }

  function setSelectLoading(isLoading) {
    stateSelect.disabled = isLoading;
    stateSelect.options[0].textContent = isLoading ? 'Loading states...' : 'Select Your State';
  }

  function populateStates() {
    const selectedValue = stateSelect.value;
    const firstOption = stateSelect.options[0];
    stateSelect.replaceChildren(firstOption);

    const statesByPostal = new Map();
    repsData.forEach((rep) => {
      if (rep.State && rep.Postal && !statesByPostal.has(rep.Postal)) {
        statesByPostal.set(rep.Postal, rep.State);
      }
    });

    Array.from(statesByPostal.entries())
      .sort((first, second) => first[1].localeCompare(second[1]))
      .forEach(([postal, state]) => {
        const option = document.createElement('option');
        option.value = postal;
        option.textContent = state;
        stateSelect.appendChild(option);
      });

    if (statesByPostal.has(selectedValue)) stateSelect.value = selectedValue;
  }

  function loadReps() {
    if (repsRequest) return repsRequest;

    setSelectLoading(true);
    setStatus('Loading representatives...');

    repsRequest = fetch(REPS_URL)
      .then((response) => {
        if (!response.ok) throw new Error('Representative data failed to load.');
        return response.json();
      })
      .then((data) => {
        repsData = Array.isArray(data) ? data : [];
        populateStates();
        setStatus('Select your state to view your representative.');
      })
      .catch((error) => {
        console.error('Error loading reps data:', error);
        setStatus('Representative lookup is unavailable. Please try again later.');
      })
      .finally(() => {
        setSelectLoading(false);
      });

    return repsRequest;
  }

  /* Context is display-only -- nothing here is ever transmitted. */
  function getProductDetails(trigger) {
    const title = (trigger && trigger.dataset && trigger.dataset.productTitle) || '';
    const imageSrc = (trigger && trigger.dataset && trigger.dataset.productImage) || '';
    return {
      title: title || 'Product pricing',
      imageSrc: imageSrc,
      imageAlt: title || 'Product pricing'
    };
  }

  function setProductPreview(details) {
    productTitle.textContent = details.title;

    if (details.imageSrc) {
      productImage.src = details.imageSrc;
      productImage.alt = details.imageAlt;
      productImage.hidden = false;
    } else {
      productImage.removeAttribute('src');
      productImage.alt = '';
      productImage.hidden = true;
    }
  }

  function getInitials(name) {
    return (name || 'CM')
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part.charAt(0))
      .join('')
      .toUpperCase();
  }

  function createFallbackAvatar(name) {
    const fallback = document.createElement('span');
    fallback.className = 'sales-rep-quick-add__rep-fallback';
    fallback.setAttribute('aria-hidden', 'true');
    fallback.textContent = getInitials(name);
    return fallback;
  }

  function createRepCard(rep) {
    const group = document.createElement('div');
    group.className = 'sales-rep-quick-add__rep-group';

    const card = document.createElement('article');
    card.className = 'sales-rep-quick-add__rep';

    const name = rep.Rep || 'Catchmaster Pro Representative';
    const imageUrl = rep['Image URL'];

    if (imageUrl) {
      const image = document.createElement('img');
      image.className = 'sales-rep-quick-add__rep-image';
      image.src = imageUrl;
      image.alt = name;
      image.width = 72;
      image.height = 72;
      image.loading = 'lazy';
      image.addEventListener('error', () => {
        image.replaceWith(createFallbackAvatar(name));
      });
      card.appendChild(image);
    } else {
      card.appendChild(createFallbackAvatar(name));
    }

    const copy = document.createElement('div');
    copy.className = 'sales-rep-quick-add__rep-copy';

    const heading = document.createElement('h4');
    heading.textContent = name;
    copy.appendChild(heading);

    if (rep.Position) {
      const position = document.createElement('p');
      position.textContent = rep.Position;
      copy.appendChild(position);
    }

    const actions = document.createElement('div');
    actions.className = 'sales-rep-quick-add__rep-actions';

    if (rep['Phone number']) {
      const phone = document.createElement('a');
      phone.href = `tel:${String(rep['Phone number']).replace(/[^\d+]/g, '')}`;
      phone.textContent = `Call: ${rep['Phone number']}`;
      actions.appendChild(phone);
    }

    if (rep.email) {
      const email = document.createElement('a');
      email.href = `mailto:${rep.email}`;
      email.textContent = `Email: ${rep.email}`;
      actions.appendChild(email);
    }

    card.appendChild(copy);
    group.appendChild(card);

    if (actions.children.length) group.appendChild(actions);

    return group;
  }

  function showSelectedReps() {
    const selectedState = stateSelect.value;
    clearRepList();

    if (!selectedState) {
      setStatus('Select your state to view your representative.');
      return;
    }

    const reps = repsData.filter((rep) => rep.Postal === selectedState);

    if (!reps.length) {
      setStatus('No representative was found for that state. Please select another state.');
      return;
    }

    setStatus(`${reps.length} representative${reps.length === 1 ? '' : 's'} found.`);
    reps.forEach((rep) => repList.appendChild(createRepCard(rep)));
  }

  function getFocusableElements() {
    return Array.from(
      modal.querySelectorAll(
        'a[href], button:not([disabled]), select:not([disabled]), textarea:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter((element) => {
      const rect = element.getBoundingClientRect();
      return rect.width > 0 && rect.height > 0;
    });
  }

  function openModal(trigger) {
    activeTrigger = trigger || null;
    lastFocusedElement = document.activeElement;
    setProductPreview(getProductDetails(trigger));
    clearRepList();
    stateSelect.value = '';
    setStatus(repsData.length ? 'Select your state to view your representative.' : 'Loading representatives...');

    modal.hidden = false;
    document.body.classList.add('sales-rep-quick-add-lock');
    if (activeTrigger) activeTrigger.setAttribute('aria-expanded', 'true');
    dialog.focus({ preventScroll: true });
    loadReps();
  }

  function closeModal() {
    if (modal.hidden) return;

    modal.hidden = true;
    document.body.classList.remove('sales-rep-quick-add-lock');

    if (activeTrigger) activeTrigger.setAttribute('aria-expanded', 'false');
    if (lastFocusedElement && typeof lastFocusedElement.focus === 'function') {
      lastFocusedElement.focus({ preventScroll: true });
    }

    activeTrigger = null;
    lastFocusedElement = null;
  }

  /* Called on init for static triggers, and by the section after it re-renders. */
  function enhanceTriggers(root) {
    if (!root || typeof root.querySelectorAll !== 'function') return;
    const scope = [];
    if (root.matches && root.matches(TRIGGER_SELECTOR)) scope.push(root);
    root.querySelectorAll(TRIGGER_SELECTOR).forEach((trigger) => scope.push(trigger));

    scope.forEach((trigger) => {
      trigger.setAttribute('aria-haspopup', 'dialog');
      trigger.setAttribute('aria-controls', 'SalesRepQuickAdd');
      if (!trigger.hasAttribute('aria-expanded')) trigger.setAttribute('aria-expanded', 'false');
      if (trigger.tagName === 'BUTTON') trigger.type = 'button';
    });
  }

  document.addEventListener('click', function (event) {
    const trigger = event.target.closest(TRIGGER_SELECTOR);
    if (!trigger) return;
    event.preventDefault();
    openModal(trigger);
  });

  document.addEventListener('click', function (event) {
    if (event.target.closest('[data-sales-rep-close]')) {
      event.preventDefault();
      closeModal();
    }
  });

  document.addEventListener('keydown', function (event) {
    if (modal.hidden) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      closeModal();
      return;
    }

    if (event.key !== 'Tab') return;

    const focusableElements = getFocusableElements();
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (!firstElement || !lastElement) {
      event.preventDefault();
      return;
    }

    if (event.shiftKey && document.activeElement === firstElement) {
      event.preventDefault();
      lastElement.focus();
    } else if (!event.shiftKey && document.activeElement === lastElement) {
      event.preventDefault();
      firstElement.focus();
    }
  });

  stateSelect.addEventListener('change', showSelectedReps);

  enhanceTriggers(document);
  loadReps();

  /* Namespaced away from the modal's id="SalesRepQuickAdd": named-element access
     would make window.SalesRepQuickAdd resolve to the DOM node before this runs. */
  window.CatchmasterSalesRep = {
    open: openModal,
    close: closeModal,
    enhance: enhanceTriggers
  };
})();
